import React, { useState, useEffect } from "react";
import "../../styles/Login.css";
import { redirectToGoogleLogin, requestUserLoginOtp, verifyUserLoginOtp } from "../../api/userAuthApi";

export default function Login({ onClose, onSignupRedirect, onLoginSuccess }) {
  // --- STATE SYSTEM MATRIX ---
  const [currentStep, setCurrentStep] = useState("request"); // "request" | "verify"
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpValues, setOtpValues] = useState(new Array(6).fill(""));
  const [countdown, setCountdown] = useState(60); 
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [authError, setAuthError] = useState("");

  // Countdown timer logic for Resend OTP
  useEffect(() => {
    let timer;
    if (currentStep === "verify" && countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [currentStep, countdown]);

  useEffect(() => {
    const [routeHash, queryString = ""] = window.location.hash.slice(1).split("?");
    if (routeHash !== "login") return;

    const params = new URLSearchParams(queryString);
    const error = params.get("googleError")
      || (params.has("sessionExpired")
        ? "Your account is no longer available. You have been logged out."
        : "");
    if (error) {
      setAuthError(error);
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#login`,
      );
    }
  }, []);

  const handleGoogleLogin = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      alert("Please accept the Terms and Conditions to continue.");
      return;
    }
    redirectToGoogleLogin();
  };

  // --- STEP 1: OTP REQUEST ---
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      alert("Please accept the Terms and Conditions to continue.");
      return;
    }
    if (!phoneNumber || phoneNumber.length < 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }
    try {
      await requestUserLoginOtp(phoneNumber);
      setCountdown(60);
      setCurrentStep("verify");
    } catch (error) {
      alert(error.message);
    }
  };

  // --- STEP 2: VERIFY OTP & COMPLETE LOGIN ---
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const fullOtp = otpValues.join("");
    if (fullOtp.length !== 6) {
      alert("Please enter the complete 6-digit OTP code.");
      return;
    }

    try {
      const response = await verifyUserLoginOtp(phoneNumber, fullOtp);
      if (response.action === "signup" || response.requiresRegistration) {
        if (onSignupRedirect) {
          onSignupRedirect(phoneNumber, response.registrationToken);
          return;
        }
        throw new Error("No account found for this mobile number.");
      }
      if (onLoginSuccess) onLoginSuccess(response.user, response.accessToken);
      if (onClose) onClose();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;

    const updatedValues = [...otpValues];
    updatedValues[index] = element.value;
    setOtpValues(updatedValues);

    if (element.value !== "" && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpValues[index] && e.target.previousSibling) {
      e.target.previousSibling.focus();
    }
  };

  const handleResend = async () => {
    try {
      await requestUserLoginOtp(phoneNumber);
      setCountdown(60);
      setOtpValues(new Array(6).fill(""));
      alert("A fresh 6-digit verification code has been sent!");
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div className="cd-login-component-parent-wrapper" style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div className="cd-login-card-main">
        
        {/* LEFT PANEL: VALUE PROPOSITION */}
        <div className="cd-login-left-brand">
          <div className="cd-brand-glow-orb"></div>
          <div className="cd-brand-dots-left"></div>
          
          <div className="cd-brand-logo-block">
            <div className="cd-logo-icon-shell">
              <span className="cd-logo-triangle">▶</span>
            </div>
            <div className="cd-logo-text-stack">
              <h2>Creators <span>Dost</span></h2>
              <p>LEARN • CREATE • GROW • TOGETHER</p>
            </div>
          </div>

          <h1 className="cd-brand-main-title">
            Empowering Creators.<br />
            <span>Building Futures.</span>
          </h1>
          <div className="cd-brand-accent-bar"></div>
          
          <p className="cd-brand-subtext">
            India's leading creator learning platform to help you learn, grow and build a successful career in the digital world.
          </p>

          <div className="cd-brand-features-list">
            <div className="cd-feature-node">
              <div className="cd-feature-icon-ring blue-ring">📖</div>
              <div className="cd-feature-text">
                <h4>100+ Premium Courses</h4>
                <p>Learn from industry experts</p>
              </div>
            </div>
            <div className="cd-feature-node">
              <div className="cd-feature-icon-ring indigo-ring">👥</div>
              <div className="cd-feature-text">
                <h4>Community Access</h4>
                <p>Connect with 50,000+ creators</p>
              </div>
            </div>
            <div className="cd-feature-node">
              <div className="cd-feature-icon-ring gold-ring">🏅</div>
              <div className="cd-feature-text">
                <h4>Certificates</h4>
                <p>Earn industry-recognized certificates</p>
              </div>
            </div>
            <div className="cd-feature-node">
              <div className="cd-feature-icon-ring cyan-ring">🚀</div>
              <div className="cd-feature-text">
                <h4>Career Growth</h4>
                <p>Get mentorship and build your brand</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: INTERACTIVE FORM */}
        <div className="cd-login-right-form">
          
          <button className="cd-modal-box-inner-close-btn" onClick={onClose} aria-label="Close modal">
            <span className="close-icon-vector">✕</span>
          </button>

          {/* STEP 1: ENTER MOBILE NUMBER */}
          {currentStep === "request" && (
            <div className="cd-form-inner-frame cd-step-fade-in">
              <div className="cd-form-header-text">
                <h2>Welcome Back!</h2>
                <p>Login to continue your learning journey</p>
                {authError && <p role="alert">{authError}</p>}
              </div>

              <div className="cd-form-method-tabs">
                <button className="cd-method-tab-btn tab-active">
                  <span className="tab-icon-space">📱</span> Mobile OTP Login
                </button>
              </div>

              <form onSubmit={handleSendOTP} className="cd-input-fields-group">
                <label className="cd-input-field-label">Mobile Number</label>
                
                <div className="cd-phone-input-combo-box">
                  <div className="cd-phone-country-dropdown cd-locked-prefix">
                    <span className="cd-flag-emoji">🇮🇳</span>
                    <span className="cd-country-code-lbl">+91</span>
                  </div>

                  <input 
                    type="tel" 
                    required
                    maxLength="10"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter your 10-digit mobile number" 
                    className="cd-main-text-input input-phone-padding"
                  />
                </div>
                
                <button type="submit" className="cd-form-submit-primary-blue-btn">
                  Send OTP <span className="cd-btn-send-arrow">➔</span>
                </button>
                <p className="cd-form-input-caption-hint">We will send a 6-digit OTP to your mobile number</p>
                <label className="cd-terms-consent-row" htmlFor="cd-login-terms-consent">
                  <input
                    id="cd-login-terms-consent"
                    type="checkbox"
                    required
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <span>
                    I accept the{" "}
                    <a href="#terms-and-conditions" target="_blank" rel="noopener noreferrer">
                      Terms and Conditions of Creators Dost
                    </a>
                  </span>
                </label>
              </form>

              <div className="cd-form-ui-separator">
                <div className="cd-separator-line"></div>
                <span>or</span>
                <div className="cd-separator-line"></div>
              </div>

              <button type="button" className="cd-form-google-sso-white-btn" onClick={handleGoogleLogin}>
                <svg className="cd-google-icon-svg" viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <div className="cd-form-security-badge-footer">
                <span className="cd-shield-vector">🛡️</span> Your data is 100% secure with us
              </div>
              
            </div>
          )}

          {/* STEP 2: VERIFY OTP */}
          {currentStep === "verify" && (
            <div className="cd-form-inner-frame cd-step-fade-in">
              <div className="cd-form-header-text">
                <h2>Verify OTP</h2>
                <p className="cd-verify-subtitle-summary">
                  We sent a 6-digit code to <br />
                  <strong>+91 {phoneNumber}</strong> 
                  <span className="cd-change-phone-trigger-link" onClick={() => setCurrentStep("request")}>
                    Change Number
                  </span>
                </p>
              </div>

              <form onSubmit={handleVerifyOTP} className="cd-input-fields-group">
                <label className="cd-input-field-label">Enter 6-Digit Code</label>
                
                <div className="cd-otp-boxes-horizontal-row">
                  {otpValues.map((val, idx) => (
                    <input
                      key={idx}
                      type="text"
                      maxLength="1"
                      required
                      autoFocus={idx === 0}
                      value={val}
                      onChange={(e) => handleOtpChange(e.target, idx)}
                      onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                      className="cd-otp-square-input-node"
                    />
                  ))}
                </div>

                <button type="submit" className="cd-form-submit-primary-blue-btn">
                  Verify & Continue <span className="cd-btn-send-arrow">➔</span>
                </button>

                <div className="cd-otp-resend-action-track">
                  {countdown > 0 ? (
                    <p>Resend OTP in <span>0:{countdown < 10 ? `0${countdown}` : countdown}</span></p>
                  ) : (
                    <p>
                      Didn't receive code?{" "}
                      <span className="cd-resend-active-link" onClick={handleResend}>
                        Resend OTP
                      </span>
                    </p>
                  )}
                </div>
              </form>

              <div className="cd-form-security-badge-footer" style={{ marginTop: '48px' }}>
                <span className="cd-shield-vector">🛡️</span> Encrypted 256-Bit SSL Authorization Active
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}